/**
 * CommandCenter - Reorganized Dashboard Layout
 * Features:
 * - Cleaner visual hierarchy with grouped sections
 * - Unified InsightCard component for AI narratives
 * - Reduced duplicate information
 * - Balanced card layout with consistent spacing
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import PageContainer from '@/components/layout/PageContainer'
import FleetHealthScore from './FleetHealthScore'
import CriticalAlertsBanner from './CriticalAlertsBanner'
import ActionQueue from './ActionQueue'
import FleetOverview from './FleetOverview'
import { Card, InsightCard } from '@/components/common'
import { FleetIntelligencePanel } from '@/components/reports'
import { narrativeGenerator } from '@/narratives'
import { useAlertStore, useUIStore } from '@/store'
import { get } from '@/services/api'
import { ENDPOINTS } from '@/services/endpoints'
import { assetApi, FleetAsset, FleetMetric, FleetAIInsight, ModelPerformance } from '@/services/assetApi'
import clsx from 'clsx'
import {
  RefreshCw,
  Download,
  AlertCircle,
  Brain,
  Activity,
  Server,
  Gauge,
  ShieldCheck,
  AlertTriangle,
  Clock,
  Wifi,
  WifiOff
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface HealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy'
  timestamp: string
  uptime_seconds: number
  version: string
  components: Array<{
    name: string
    status: string
    message: string
  }>
  models: Array<{
    asset_id: string
    model_loaded: boolean
  }>
  issues: string[]
}

interface AlertsResponse {
  alerts: Array<{
    id: string
    asset_id: string
    severity: string
    message: string
  }>
  total: number
}

interface ModelsResponse {
  models: Array<{
    asset_id: string
    model_type: string
    created_at: string
  }>
  total: number
}

interface FleetData {
  healthScore: number
  totalAssets: number
  healthyAssets: number
  warningAssets: number
  criticalAssets: number
  anomaliesToday: number
  previousHealthScore: number
  apiStatus: 'healthy' | 'degraded' | 'unhealthy'
  uptime: number
  version: string
}

// ============================================================================
// FLEET DATA STATE (fetched from API)
// ============================================================================

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Compact status indicator for system health */
function SystemStatusBar({ data, loading }: { data: FleetData; loading: boolean }) {
  const isOnline = data.apiStatus === 'healthy' || data.apiStatus === 'degraded'

  return (
    <div className="flex items-center gap-6 px-4 py-2 bg-surface-900/50 rounded-lg border border-surface-700/50">
      {/* Connection Status */}
      <div className="flex items-center gap-2">
        {isOnline ? (
          <Wifi size={14} className="text-emerald-400" />
        ) : (
          <WifiOff size={14} className="text-red-400" />
        )}
        <span className={clsx(
          'text-xs font-medium uppercase',
          isOnline ? 'text-emerald-400' : 'text-red-400'
        )}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>

      <div className="w-px h-4 bg-surface-700" />

      {/* API Status */}
      <div className="flex items-center gap-2">
        <Server size={14} className="text-surface-500" />
        <span className="text-xs text-surface-400">API:</span>
        <span className={clsx(
          'text-xs font-medium',
          data.apiStatus === 'healthy' ? 'text-emerald-400' :
          data.apiStatus === 'degraded' ? 'text-amber-400' : 'text-red-400'
        )}>
          {data.apiStatus.toUpperCase()}
        </span>
      </div>

      <div className="w-px h-4 bg-surface-700" />

      {/* Uptime */}
      <div className="flex items-center gap-2">
        <Clock size={14} className="text-surface-500" />
        <span className="text-xs text-surface-400">Uptime:</span>
        <span className="text-xs font-mono text-surface-300">
          {Math.floor(data.uptime / 3600)}h {Math.floor((data.uptime % 3600) / 60)}m
        </span>
      </div>

      <div className="w-px h-4 bg-surface-700" />

      {/* Version */}
      <div className="flex items-center gap-2">
        <Activity size={14} className="text-surface-500" />
        <span className="text-xs text-surface-400">v{data.version || '2.0.0'}</span>
      </div>

      {/* Loading indicator */}
      {loading && (
        <>
          <div className="w-px h-4 bg-surface-700" />
          <RefreshCw size={14} className="text-primary-400 animate-spin" />
        </>
      )}
    </div>
  )
}

/** Quick stats summary cards */
function QuickStatCard({
  label,
  value,
  icon: Icon,
  status
}: {
  label: string
  value: number
  icon: React.ElementType
  status: 'good' | 'warning' | 'critical' | 'neutral'
}) {
  const statusConfig = {
    good: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: 'text-emerald-400' },
    warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: 'text-amber-400' },
    critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: 'text-red-400' },
    neutral: { bg: 'bg-surface-800', border: 'border-surface-700', text: 'text-surface-300', icon: 'text-surface-400' }
  }

  const config = statusConfig[status]

  return (
    <div className={clsx(
      'flex items-center gap-3 p-4 rounded-xl border transition-all hover:scale-[1.02]',
      config.bg,
      config.border
    )}>
      <div className={clsx('p-2 rounded-lg', config.bg)}>
        <Icon size={20} className={config.icon} />
      </div>
      <div>
        <p className="text-xs text-surface-400 uppercase tracking-wider">{label}</p>
        <p className={clsx('text-2xl font-mono font-bold', config.text)}>{value}</p>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CommandCenter() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showIntelligencePanel, setShowIntelligencePanel] = useState(false)
  const [usingMockData, setUsingMockData] = useState(false)
  const [fleetData, setFleetData] = useState<FleetData>({
    healthScore: 0,
    totalAssets: 0,
    healthyAssets: 0,
    warningAssets: 0,
    criticalAssets: 0,
    anomaliesToday: 0,
    previousHealthScore: 0,
    apiStatus: 'healthy',
    uptime: 0,
    version: '2.0.0'
  })
  const [fleetInsight, setFleetInsight] = useState<string>('')
  const { setAlerts } = useAlertStore()
  const { addNotification, setApiStatus } = useUIStore()

  // Fleet Intelligence Panel state
  const [fleetAssets, setFleetAssets] = useState<FleetAsset[]>([])
  const [fleetMetrics, setFleetMetrics] = useState<FleetMetric[]>([])
  const [fleetAIInsights, setFleetAIInsights] = useState<FleetAIInsight[]>([])
  const [modelPerformance, setModelPerformance] = useState<ModelPerformance>({
    accuracy: 0.91,  // Default to 91% - will be updated from API
    precision: 0.89,
    recall: 0.87,
    f1Score: 0.88
  })
  const [intelligenceLoading, setIntelligenceLoading] = useState(false)

  // Memoized insight metrics for the unified InsightCard
  const insightMetrics = useMemo(() => [
    {
      label: 'Health',
      value: fleetData.healthScore,
      unit: '%',
      status: fleetData.healthScore >= 85 ? 'good' as const :
              fleetData.healthScore >= 60 ? 'warning' as const : 'critical' as const,
      trend: fleetData.healthScore > fleetData.previousHealthScore ? 'up' as const :
             fleetData.healthScore < fleetData.previousHealthScore ? 'down' as const : 'stable' as const
    },
    {
      label: 'Assets',
      value: fleetData.totalAssets,
      status: 'neutral' as const
    },
    {
      label: 'Warnings',
      value: fleetData.warningAssets,
      status: fleetData.warningAssets > 0 ? 'warning' as const : 'good' as const
    },
    {
      label: 'Critical',
      value: fleetData.criticalAssets,
      status: fleetData.criticalAssets > 0 ? 'critical' as const : 'good' as const
    }
  ], [fleetData])

  // Recommended actions based on fleet state - with navigation handlers
  const insightActions = useMemo(() => {
    const actions = []
    if (fleetData.criticalAssets > 0) {
      actions.push({
        id: 'review-critical',
        label: 'Review Critical Alerts',
        description: `${fleetData.criticalAssets} asset(s) need immediate attention`,
        priority: 'immediate' as const,
        onClick: () => {
          addNotification({
            type: 'info',
            title: 'Opening Asset Intelligence',
            message: `Navigating to review ${fleetData.criticalAssets} critical asset(s)...`
          })
          navigate('/assets')
        }
      })
    }
    if (fleetData.warningAssets > 0) {
      actions.push({
        id: 'schedule-inspection',
        label: 'Schedule Preventive Inspection',
        description: `${fleetData.warningAssets} warning(s) detected`,
        priority: 'soon' as const,
        onClick: () => {
          addNotification({
            type: 'info',
            title: 'Opening Maintenance Intelligence',
            message: `Navigating to schedule inspections for ${fleetData.warningAssets} asset(s)...`
          })
          navigate('/maintenance')
        }
      })
    }
    return actions
  }, [fleetData, addNotification, navigate])

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    setApiStatus('connecting')

    try {
      // Fetch all data in parallel
      const [healthData, alertsData, modelsData, assetsResult] = await Promise.all([
        get<HealthResponse>(ENDPOINTS.HEALTH),
        get<AlertsResponse>(ENDPOINTS.ALERTS),
        get<ModelsResponse>(ENDPOINTS.MODELS),
        assetApi.getAssetsWithFallback()
      ])

      // Use assets from assetApi for accurate count
      const assetsData = Array.isArray(assetsResult?.data) ? assetsResult.data : []
      const totalAssets = assetsData.length || modelsData.total || 0
      setUsingMockData(assetsResult?.isMock ?? false)
      const loadedModels = healthData.models?.filter(m => m.model_loaded).length || 0
      const criticalAlerts = alertsData.alerts?.filter(a => a.severity === 'critical').length || 0
      const warningAlerts = alertsData.alerts?.filter(a => a.severity === 'warning').length || 0

      // Calculate health score from assets if available
      let healthScore = 100
      if (assetsData.length > 0) {
        healthScore = Math.round(assetsData.reduce((sum, a) => sum + (a.health_score || 0), 0) / assetsData.length)
      } else {
        if (healthData.status === 'degraded') healthScore = 75
        if (healthData.status === 'unhealthy') healthScore = 50
        healthScore -= healthData.issues?.length * 5 || 0
      }
      healthScore = Math.max(0, Math.min(100, healthScore))

      // Calculate healthy/warning/critical counts from assets
      const healthyCount = assetsData.filter(a => a.status === 'healthy').length
      const warningCount = assetsData.filter(a => a.status === 'warning').length || warningAlerts
      const criticalCount = assetsData.filter(a => a.status === 'critical').length || criticalAlerts

      const newFleetData: FleetData = {
        healthScore,
        totalAssets,
        healthyAssets: healthyCount || loadedModels,
        warningAssets: warningCount,
        criticalAssets: criticalCount,
        anomaliesToday: alertsData.total || 0,
        // Use actual previous health if available, otherwise same as current
        // NOTE: Real historical data would come from a dedicated API endpoint
        previousHealthScore: healthScore,
        apiStatus: healthData.status,
        uptime: healthData.uptime_seconds,
        version: healthData.version || '2.0.0'
      }

      setFleetData(newFleetData)

      if (alertsData.alerts) {
        const now = new Date().toISOString()
        setAlerts(alertsData.alerts.map(a => {
          let severity: 'critical' | 'high' | 'medium' | 'low' | 'info' = 'info'
          if (a.severity === 'critical') severity = 'critical'
          else if (a.severity === 'warning') severity = 'high'
          else if (a.severity === 'info') severity = 'info'
          else severity = 'medium'

          return {
            id: a.id,
            asset_id: a.asset_id,
            severity,
            status: 'active' as const,
            title: `Alert: ${a.asset_id}`,
            message: a.message,
            created_at: now,
            updated_at: now
          }
        }))
      }

      const insight = narrativeGenerator.generateFleetInsight({
        healthScore: newFleetData.healthScore,
        totalAssets: newFleetData.totalAssets,
        healthyAssets: newFleetData.healthyAssets,
        warningAssets: newFleetData.warningAssets,
        criticalAssets: newFleetData.criticalAssets,
        anomaliesToday: newFleetData.anomaliesToday,
        previousHealthScore: newFleetData.previousHealthScore
      })
      setFleetInsight(insight)

      setApiStatus('connected')

    } catch (err) {
      console.error('Failed to fetch fleet data:', err)
      const errorMessage = 'Failed to connect to API. Make sure the server is running.'
      setError(errorMessage)
      setApiStatus('error', errorMessage)

      const fallbackData: FleetData = {
        healthScore: 0,
        totalAssets: 0,
        healthyAssets: 0,
        warningAssets: 0,
        criticalAssets: 0,
        anomaliesToday: 0,
        previousHealthScore: 0,
        apiStatus: 'unhealthy',
        uptime: 0,
        version: '2.0.0'
      }
      setFleetData(fallbackData)
      setFleetInsight('Unable to generate insights - API connection failed. Check that the backend server is running and accessible.')
    } finally {
      setLoading(false)
    }
  }, [setAlerts, setApiStatus])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Fetch fleet intelligence data when panel is shown
  useEffect(() => {
    const fetchIntelligenceData = async () => {
      if (!showIntelligencePanel) return

      setIntelligenceLoading(true)
      try {
        // Fetch assets first
        const assetsResult = await assetApi.getAssetsWithFallback()

        // Then fetch fleet assets, metrics, insights, and model performance in parallel
        const [fleetAssetsResult, metricsResult, modelPerfResult] = await Promise.all([
          assetApi.getFleetAssetsWithFallback(),
          assetApi.getFleetMetricsWithFallback(),
          assetApi.getFleetModelPerformance()
        ])

        // Get insights based on assets
        const insightsResult = await assetApi.getFleetInsightsWithFallback(assetsResult.data)

        setFleetAssets(fleetAssetsResult.data)
        setFleetMetrics(metricsResult.data)
        setFleetAIInsights(insightsResult.data)
        setModelPerformance(modelPerfResult.data)
      } catch (error) {
        console.error('Error fetching intelligence data:', error)
      } finally {
        setIntelligenceLoading(false)
      }
    }

    fetchIntelligenceData()
  }, [showIntelligencePanel])

  const handleRefresh = () => {
    fetchData()
    addNotification({
      type: 'info',
      title: 'Refreshing',
      message: 'Fetching latest data from API...'
    })
  }

  const handleExport = async () => {
    addNotification({
      type: 'info',
      title: 'Export Started',
      message: 'Generating fleet report...'
    })

    const exportData = {
      timestamp: new Date().toISOString(),
      fleetData,
      insight: fleetInsight
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `fleet-report-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)

    addNotification({
      type: 'success',
      title: 'Export Complete',
      message: 'Fleet report downloaded successfully'
    })
  }

  // Current fleet status data (no fake historical trends)
  // NOTE: Historical trend data would require a dedicated API endpoint
  const fleetStatus = useMemo(() => {
    // Calculate current anomaly level based on fleet composition
    const anomalyLevel = fleetData.totalAssets > 0
      ? Math.round((fleetData.criticalAssets * 100 + fleetData.warningAssets * 50) / fleetData.totalAssets)
      : 0

    // Fleet composition breakdown
    const healthyPercent = fleetData.totalAssets > 0
      ? Math.round((fleetData.healthyAssets / fleetData.totalAssets) * 100)
      : 0
    const warningPercent = fleetData.totalAssets > 0
      ? Math.round((fleetData.warningAssets / fleetData.totalAssets) * 100)
      : 0
    const criticalPercent = fleetData.totalAssets > 0
      ? Math.round((fleetData.criticalAssets / fleetData.totalAssets) * 100)
      : 0

    return {
      healthScore: fleetData.healthScore,
      anomalyLevel,
      composition: {
        healthy: healthyPercent,
        warning: warningPercent,
        critical: criticalPercent
      },
      // Status indicator based on current state
      status: fleetData.criticalAssets > 0 ? 'critical' :
              fleetData.warningAssets > 2 ? 'warning' :
              fleetData.healthScore >= 85 ? 'healthy' : 'monitoring'
    }
  }, [fleetData])

  return (
    <PageContainer
      title="Command Center"
      subtitle="Real-time fleet monitoring and AI-powered insights"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowIntelligencePanel(!showIntelligencePanel)}
            className={clsx(
              'btn flex items-center gap-2',
              showIntelligencePanel ? 'btn-primary' : 'btn-secondary'
            )}
          >
            <Brain size={16} />
            {showIntelligencePanel ? 'Hide' : 'Show'} Deep Analysis
          </button>
          <button
            onClick={handleRefresh}
            className="btn btn-secondary flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Download size={16} />
            Export
          </button>
        </div>
      }
    >
      {/* ================================================================== */}
      {/* SECTION 1: System Status Bar (Consolidated) */}
      {/* ================================================================== */}
      <div className="mb-4">
        <SystemStatusBar data={fleetData} loading={loading} />
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
          <AlertCircle className="text-red-400 mt-0.5 flex-shrink-0" size={20} />
          <div>
            <p className="text-red-400 font-medium">{error}</p>
            <p className="text-sm text-surface-400 mt-1">
              Check that the backend server is running and accessible. The dashboard will auto-reconnect when available.
            </p>
          </div>
        </div>
      )}

      {/* Data Source Indicator */}
      {usingMockData ? (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
          <AlertTriangle className="text-amber-400 flex-shrink-0" size={18} />
          <div className="flex-1">
            <p className="text-amber-400 font-medium text-sm">Displaying Simulated Data</p>
            <p className="text-xs text-surface-400">
              Mock data mode is enabled in settings. Disable it to see live API data.
            </p>
          </div>
          <span className="px-2 py-0.5 text-xs font-mono bg-amber-500/20 text-amber-400 rounded">
            MOCK
          </span>
        </div>
      ) : !loading && fleetData.totalAssets > 0 && (
        <div className="mb-4 p-2 bg-status-healthy/5 border border-status-healthy/20 rounded-xl flex items-center gap-3">
          <Activity className="text-status-healthy flex-shrink-0" size={16} />
          <p className="text-status-healthy text-sm">
            Live API Data • {fleetData.totalAssets} assets • Last sync: {new Date().toLocaleTimeString()}
          </p>
          <span className="ml-auto px-2 py-0.5 text-xs font-mono bg-status-healthy/20 text-status-healthy rounded">
            LIVE
          </span>
        </div>
      )}

      {/* Critical Alerts Banner */}
      <CriticalAlertsBanner />

      {/* ================================================================== */}
      {/* SECTION 2: Fleet Intelligence Panel (Optional Deep Dive) */}
      {/* ================================================================== */}
      {showIntelligencePanel && (
        <div className="mb-6">
          {intelligenceLoading ? (
            <Card>
              <div className="flex items-center justify-center h-48">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-surface-400">Loading fleet intelligence...</span>
                </div>
              </div>
            </Card>
          ) : (
            <FleetIntelligencePanel
              fleetName="Natural Gas Compression Fleet"
              totalAssets={fleetAssets.length || fleetData.totalAssets}
              assets={fleetAssets}
              metrics={fleetMetrics}
              insights={fleetAIInsights}
              modelPerformance={modelPerformance}
              connectionStatus={
                fleetData.apiStatus === 'healthy' ? 'connected' :
                fleetData.apiStatus === 'degraded' ? 'connecting' :
                loading ? 'connecting' : 'error'
              }
              isLoading={intelligenceLoading}
              isMockData={usingMockData}
              lastUpdated={new Date().toLocaleTimeString()}
              error={error}
              onRetry={handleRefresh}
            />
          )}
        </div>
      )}

      {/* ================================================================== */}
      {/* SECTION 3: Primary Dashboard Grid */}
      {/* ================================================================== */}
      <div className="grid grid-cols-12 gap-6">

        {/* ----- LEFT COLUMN: Health & Quick Stats ----- */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* Fleet Health Score - Hero Card */}
          <FleetHealthScore
            score={fleetData.healthScore}
            previousScore={fleetData.previousHealthScore}
            loading={loading}
            uptime={fleetData.uptime > 0 ? Math.min(99.9, 95 + (fleetData.uptime / 86400) * 5) : undefined}
            atRiskCount={fleetData.warningAssets + fleetData.criticalAssets}
            criticalCount={fleetData.criticalAssets}
            warningCount={fleetData.warningAssets}
            totalAssets={fleetData.totalAssets}
          />

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <QuickStatCard
              label="Total Assets"
              value={fleetData.totalAssets}
              icon={Gauge}
              status={fleetData.totalAssets > 0 ? 'neutral' : 'warning'}
            />
            <QuickStatCard
              label="Models Active"
              value={fleetData.healthyAssets}
              icon={ShieldCheck}
              status={fleetData.healthyAssets > 0 ? 'good' : 'warning'}
            />
            <QuickStatCard
              label="Warnings"
              value={fleetData.warningAssets}
              icon={AlertTriangle}
              status={fleetData.warningAssets > 0 ? 'warning' : 'good'}
            />
            <QuickStatCard
              label="Critical"
              value={fleetData.criticalAssets}
              icon={AlertCircle}
              status={fleetData.criticalAssets > 0 ? 'critical' : 'good'}
            />
          </div>
        </div>

        {/* ----- RIGHT COLUMN: AI Insights & Trends ----- */}
        <div className="col-span-12 lg:col-span-8 space-y-6">

          {/* AI Intelligence Card (Using new unified component) */}
          <InsightCard
            title="Fleet Intelligence Summary"
            narrative={fleetInsight || 'Loading fleet insights...'}
            type={fleetData.apiStatus === 'unhealthy' ? 'alert' : 'analysis'}
            severity={
              fleetData.criticalAssets > 0 ? 'critical' :
              fleetData.warningAssets > 0 ? 'high' :
              fleetData.apiStatus === 'unhealthy' ? 'high' : 'info'
            }
            confidence={Math.max(0.85, fleetData.apiStatus === 'healthy' ? modelPerformance.accuracy : modelPerformance.accuracy * 0.85)}
            variant="compact"
            metrics={insightMetrics}
            actions={insightActions}
            keyPoints={[]}
            modelType="ensemble"
            timestamp={loading ? 'Loading...' : 'Just now'}
            source="Real-time fleet sensor data and historical patterns"
            expandable={false}
            showCopyButton
          />

          {/* Fleet Health Status - Real-time data only (no fake historical trends) */}
          <Card
            title="Fleet Health Status"
            subtitle="Current fleet composition"
            className="border-surface-700"
          >
            {/* Fleet Composition Bars */}
            <div className="space-y-4">
              {/* Health Score Gauge */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-surface-400">Overall Health</span>
                <span className={clsx(
                  'text-2xl font-mono font-bold',
                  fleetStatus.healthScore >= 85 ? 'text-emerald-400' :
                  fleetStatus.healthScore >= 70 ? 'text-amber-400' : 'text-red-400'
                )}>
                  {fleetStatus.healthScore}%
                </span>
              </div>
              <div className="h-3 bg-surface-800 rounded-full overflow-hidden">
                <div
                  className={clsx(
                    'h-full rounded-full transition-all duration-500',
                    fleetStatus.healthScore >= 85 ? 'bg-emerald-500' :
                    fleetStatus.healthScore >= 70 ? 'bg-amber-500' : 'bg-red-500'
                  )}
                  style={{ width: `${fleetStatus.healthScore}%` }}
                />
              </div>

              {/* Asset Composition Breakdown */}
              <div className="pt-4 border-t border-surface-700">
                <div className="text-xs text-surface-500 mb-3">Asset Distribution</div>
                <div className="flex h-8 rounded-lg overflow-hidden">
                  {fleetStatus.composition.healthy > 0 && (
                    <div
                      className="bg-emerald-500/80 flex items-center justify-center text-xs font-medium text-white"
                      style={{ width: `${fleetStatus.composition.healthy}%` }}
                      title={`${fleetStatus.composition.healthy}% Healthy`}
                    >
                      {fleetStatus.composition.healthy > 15 && `${fleetStatus.composition.healthy}%`}
                    </div>
                  )}
                  {fleetStatus.composition.warning > 0 && (
                    <div
                      className="bg-amber-500/80 flex items-center justify-center text-xs font-medium text-white"
                      style={{ width: `${fleetStatus.composition.warning}%` }}
                      title={`${fleetStatus.composition.warning}% Warning`}
                    >
                      {fleetStatus.composition.warning > 15 && `${fleetStatus.composition.warning}%`}
                    </div>
                  )}
                  {fleetStatus.composition.critical > 0 && (
                    <div
                      className="bg-red-500/80 flex items-center justify-center text-xs font-medium text-white"
                      style={{ width: `${fleetStatus.composition.critical}%` }}
                      title={`${fleetStatus.composition.critical}% Critical`}
                    >
                      {fleetStatus.composition.critical > 15 && `${fleetStatus.composition.critical}%`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="text-surface-400">Healthy ({fleetData.healthyAssets})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span className="text-surface-400">Warning ({fleetData.warningAssets})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="text-surface-400">Critical ({fleetData.criticalAssets})</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Footer */}
            <div className="mt-4 pt-3 border-t border-surface-700 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="text-surface-500">Anomaly Level:</span>
                <span className={clsx(
                  'font-mono font-medium',
                  fleetStatus.anomalyLevel < 20 ? 'text-emerald-400' :
                  fleetStatus.anomalyLevel < 40 ? 'text-amber-400' : 'text-red-400'
                )}>
                  {fleetStatus.anomalyLevel}%
                </span>
              </div>
              <div className={clsx(
                'flex items-center gap-1 px-2 py-1 rounded-lg capitalize',
                fleetStatus.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-400' :
                fleetStatus.status === 'critical' ? 'bg-red-500/10 text-red-400' :
                fleetStatus.status === 'warning' ? 'bg-amber-500/10 text-amber-400' :
                'bg-blue-500/10 text-blue-400'
              )}>
                <ShieldCheck size={12} />
                <span className="font-medium">{fleetStatus.status}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* ================================================================== */}
        {/* SECTION 4: Fleet Overview & Actions (Full Width) */}
        {/* ================================================================== */}

        {/* Fleet Asset Overview */}
        <div className="col-span-12 lg:col-span-6">
          <FleetOverview loading={loading} />
        </div>

        {/* Action Queue */}
        <div className="col-span-12 lg:col-span-6">
          <ActionQueue />
        </div>
      </div>
    </PageContainer>
  )
}
